import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TurnstileService } from './turnstile.service';
import { BiometricAuthService } from './biometric-auth.service';

export interface User {
  id: string;
  email: string;
  name: string;
  isEmailVerified: boolean;
  createdAt: string;
  lastLogin?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  turnstile_token: string;
}

export interface RegisterRequest {
  email: string;
  name: string;
  turnstile_token: string;
  accept_terms: boolean;
  marketing_consent?: boolean;
}

export interface ActivateAccountRequest {
  token: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  token?: string;
  tokens?: {
    access_token: string;
    refresh_token: string;
    access_expires_in: number;
    refresh_expires_in: number;
  };
  session?: {
    id: string;
    device_info: any;
    created_at: string;
    expires_at: string;
  };
  message?: string;
  requiresEmailVerification?: boolean;
  requiresActivation?: boolean;
}

export interface VerifyEmailRequest {
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = '/api/v1/auth';
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private refreshTimer?: number;
  private readonly REFRESH_THRESHOLD = 5 * 60;

  constructor(
    private http: HttpClient,
    private turnstileService: TurnstileService,
    private biometricAuthService: BiometricAuthService
  ) {
    this.loadUserFromStorage();
    this.initializeTurnstile();
  }

  private async initializeTurnstile(): Promise<void> {
    try {
      const enabled = await this.turnstileService.isEnabled();
      if (enabled) {
        await this.turnstileService.loadScript();
        console.log('Cloudflare Turnstile inicializado correctamente');
      }
    } catch (error) {
      console.warn('Turnstile no disponible:', error);
    }
  }

  async login(email: string, password: string, rememberSession: boolean = false): Promise<AuthResponse> {
    try {
      const turnstileToken = await this.turnstileService.getToken('turnstile-login');

      const loginRequest: LoginRequest = {
        email,
        password,
        turnstile_token: turnstileToken
      };

      return this.http.post<AuthResponse>(`${this.API_URL}/login`, loginRequest)
        .pipe(
          tap(response => {
            if (response.success && response.user) {
              const accessToken = response.tokens?.access_token || response.token;
              if (accessToken) {
                if (rememberSession) {
                  localStorage.setItem('remember_session', 'true');
                } else {
                  localStorage.removeItem('remember_session');
                }
                this.setUser(response.user, accessToken);
                if (response.tokens?.refresh_token) {
                  localStorage.setItem('refresh_token', response.tokens.refresh_token);
                }
              }
            }
          }),
          catchError(this.handleError)
        ).toPromise() as Promise<AuthResponse>;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Error durante el proceso de autenticacion'
      };
    }
  }

  async register(email: string, name: string, acceptTerms: boolean = true, marketingConsent: boolean = false): Promise<AuthResponse> {
    try {
      const turnstileToken = await this.turnstileService.getToken('turnstile-register');

      const registerRequest: RegisterRequest = {
        email,
        name,
        turnstile_token: turnstileToken,
        accept_terms: acceptTerms,
        marketing_consent: marketingConsent
      };

      return this.http.post<AuthResponse>(`${this.API_URL}/register`, registerRequest)
        .pipe(
          catchError(this.handleError)
        ).toPromise() as Promise<AuthResponse>;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Error durante el proceso de registro'
      };
    }
  }

  async activateAccount(token: string, password: string): Promise<AuthResponse> {
    try {
      const request: ActivateAccountRequest = { token, password };

      return this.http.post<AuthResponse>(`${this.API_URL}/activate`, request)
        .pipe(
          tap(response => {
            if (response.success && response.user) {
              const accessToken = response.tokens?.access_token || response.token;
              if (accessToken) {
                this.setUser(response.user, accessToken);
                if (response.tokens?.refresh_token) {
                  localStorage.setItem('refresh_token', response.tokens.refresh_token);
                }
              }
            }
          }),
          catchError(this.handleError)
        ).toPromise() as Promise<AuthResponse>;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Error al activar la cuenta'
      };
    }
  }

  verifyEmail(token: string): Observable<AuthResponse> {
    const verifyRequest: VerifyEmailRequest = { token };

    return this.http.post<AuthResponse>(`${this.API_URL}/verify-email`, verifyRequest)
      .pipe(
        tap(response => {
          if (response.success && response.user) {
            const currentUser = this.getCurrentUser();
            if (currentUser) {
              const updatedUser = { ...currentUser, isEmailVerified: true };
              const token = this.getToken();
              this.setUser(updatedUser, token || undefined);
            }
          }
        }),
        catchError(this.handleError)
      );
  }

  async resendActivationEmail(email: string): Promise<AuthResponse> {
    try {
      const turnstileToken = await this.turnstileService.getToken('turnstile-resend');

      return this.http.post<AuthResponse>(`${this.API_URL}/resend-activation`, {
        email,
        turnstile_token: turnstileToken
      }).pipe(
        catchError(this.handleError)
      ).toPromise() as Promise<AuthResponse>;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Error al reenviar email de activacion'
      };
    }
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    try {
      const turnstileToken = await this.turnstileService.getToken('turnstile-forgot');

      return this.http.post<AuthResponse>(`${this.API_URL}/forgot-password`, {
        email,
        turnstile_token: turnstileToken
      }).pipe(
        catchError(this.handleError)
      ).toPromise() as Promise<AuthResponse>;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Error al solicitar recuperacion de contrasena'
      };
    }
  }

  logout(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    this.http.post(`${this.API_URL}/logout`, {}).subscribe();

    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('remember_session');
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  verifyToken(): Observable<AuthResponse> {
    const token = this.getToken();
    if (!token) {
      return throwError('No token found');
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    return this.http.get<AuthResponse>(`${this.API_URL}/verify-token`, { headers })
      .pipe(
        tap(response => {
          if (!response.success) {
            this.logout();
          }
        }),
        catchError(error => {
          this.logout();
          return throwError(error);
        })
      );
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  shouldRememberSession(): boolean {
    return localStorage.getItem('remember_session') === 'true';
  }

  hasRememberedSession(): boolean {
    const hasTokens = !!(this.getToken() && this.getRefreshToken());
    const shouldRemember = this.shouldRememberSession();
    return hasTokens && shouldRemember;
  }

  async setupBiometricAuth(): Promise<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    try {
      await this.biometricAuthService.registerBiometric(
        currentUser.id,
        currentUser.name,
        currentUser.email
      );
      return true;
    } catch (error) {
      console.error('Error configurando autenticacion biometrica:', error);
      return false;
    }
  }

  async authenticateWithBiometrics(): Promise<User | null> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return null;
    }

    try {
      const success = await this.biometricAuthService.authenticateBiometric(currentUser.id);
      if (success) {
        return currentUser;
      }
      return null;
    } catch (error) {
      console.error('Error en autenticacion biometrica:', error);
      return null;
    }
  }

  hasBiometricAuth(): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return false;
    }
    return this.biometricAuthService.hasBiometricCredentials(currentUser.id);
  }

  isBiometricSupported(): boolean {
    return this.biometricAuthService.isSupported();
  }

  isBiometricAvailable(): boolean {
    return this.biometricAuthService.isAvailable();
  }

  getBiometricType(): string {
    return this.biometricAuthService.getBiometricType();
  }

  removeBiometricAuth(): void {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      this.biometricAuthService.removeBiometricCredentials(currentUser.id);
    }
  }

  private setUser(user: User, token?: string): void {
    localStorage.setItem('user', JSON.stringify(user));
    if (token) {
      localStorage.setItem('token', token);
      this.scheduleTokenRefresh(token);
    }
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
  }

  private loadUserFromStorage(): void {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (userStr && token) {
      try {
        const user = JSON.parse(userStr);

        if (this.isTokenExpired(token)) {
          if (refreshToken && !this.isTokenExpired(refreshToken)) {
            this.refreshTokenRequest().subscribe({
              next: (response) => {
                this.setUser(user, response.tokens?.access_token || response.token || '');
                if (response.tokens?.refresh_token) {
                  localStorage.setItem('refresh_token', response.tokens.refresh_token);
                }
              },
              error: () => {
                this.logout();
              }
            });
            return;
          } else {
            this.logout();
            return;
          }
        }

        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
        this.scheduleTokenRefresh(token);

        this.verifyToken().subscribe({
          error: () => {
            if (refreshToken && !this.isTokenExpired(refreshToken)) {
              this.refreshTokenRequest().subscribe({
                next: (response) => {
                  this.setUser(user, response.tokens?.access_token || response.token || '');
                  if (response.tokens?.refresh_token) {
                    localStorage.setItem('refresh_token', response.tokens.refresh_token);
                  }
                },
                error: () => {
                  this.logout();
                }
              });
            } else {
              this.logout();
            }
          }
        });
      } catch (error) {
        // Error de parsing, no cerrar sesion inmediatamente
      }
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return false;
      const now = Math.floor(Date.now() / 1000);
      return now >= exp;
    } catch (error) {
      console.error('Error validating token expiration:', error);
      return true;
    }
  }

  private refreshTokenRequest(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError('No refresh token found');
    }

    return this.http.post<AuthResponse>(`${this.API_URL}/refresh`, {
      refresh_token: refreshToken
    }).pipe(
      tap(response => {
        if (response.success && response.tokens?.access_token) {
          localStorage.setItem('token', response.tokens.access_token);
          if (response.tokens.refresh_token) {
            localStorage.setItem('refresh_token', response.tokens.refresh_token);
          }
          this.scheduleTokenRefresh(response.tokens.access_token);
        }
      }),
      catchError(error => {
        console.error('Error refreshing token:', error);
        return throwError(error);
      })
    );
  }

  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return;

      const now = Math.floor(Date.now() / 1000);
      const timeUntilRefresh = (exp - now - this.REFRESH_THRESHOLD) * 1000;

      if (timeUntilRefresh > 60000) {
        console.log(`Token refresh programado en ${Math.round(timeUntilRefresh / 60000)} minutos`);
        this.refreshTimer = window.setTimeout(() => {
          this.performAutomaticRefresh();
        }, timeUntilRefresh);
      } else if (timeUntilRefresh > 0) {
        setTimeout(() => this.performAutomaticRefresh(), 5000);
      }
    } catch (error) {
      console.error('Error programando refresh automatico:', error);
    }
  }

  private performAutomaticRefresh(): void {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      console.log('Refresh token expirado o no disponible, cerrando sesion');
      this.logout();
      return;
    }

    console.log('Realizando refresh automatico del token...');
    this.refreshTokenRequest().subscribe({
      next: () => {
        console.log('Token refrescado automaticamente');
      },
      error: (error) => {
        console.error('Error en refresh automatico, cerrando sesion:', error);
        this.logout();
      }
    });
  }

  getUserProfile(): Observable<any> {
    const token = this.getToken();
    if (!token) {
      return throwError({ success: false, message: 'No token available' });
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.get('/api/v1/users/profile', { headers }).pipe(
      catchError(this.handleError)
    );
  }

  updateUserProfile(profileData: any): Observable<any> {
    const token = this.getToken();
    if (!token) {
      return throwError({ success: false, message: 'No token available' });
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    return this.http.put('/api/v1/users/profile', profileData, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any): Observable<never> {
    console.error('Auth error:', error);

    let message = 'Error desconocido';
    if (error.error?.message) {
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    }

    return throwError({
      success: false,
      message
    });
  }
}
